import { useCallback, useState } from 'react';

import {
  fetchRequestLoadDetail,
  updateRequestLoadStatus,
  voidWeighingRequest,
} from './kioskApi';
import {
  createRequestLoadSummaryState,
  isCompletedRequest,
  mergeRequestLoad,
  mergeRequests,
} from './kioskTransforms';

export function useKioskLoadDetail({
  createRequestDirectionRef,
  operatorActorName,
  operatorSource,
  setCreateRequestDirection,
  setCurrentRequest,
  setReadings,
  setRequestLoads,
  setRequestState,
  setRequests,
}) {
  const [detailLoadId, setDetailLoadId] = useState('');
  const [detailLoadState, setDetailLoadState] = useState({ type: 'idle', message: '' });
  const [detailLoad, setDetailLoad] = useState(null);
  const [detailLoadSummary, setDetailLoadSummary] = useState(() => createRequestLoadSummaryState());

  const openLoadDetailModal = useCallback(async (loadId) => {
    setDetailLoadId(loadId);
    setDetailLoadState({ type: 'pending', message: 'Loading load detail...' });
    setDetailLoadSummary(createRequestLoadSummaryState({ status: 'pending' }));

    try {
      const { detail, requests: relatedRequests } = await fetchRequestLoadDetail(loadId);
      setDetailLoad(detail);
      setDetailLoadSummary(createRequestLoadSummaryState({
        requests: relatedRequests,
        requestCount: relatedRequests.filter(isCompletedRequest).length,
        status: 'success',
      }));
      setDetailLoadState({ type: 'success', message: '' });
    } catch (error) {
      setDetailLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to load request load detail.',
      });
    }
  }, []);

  const closeLoadDetailModal = useCallback(() => {
    setDetailLoadId('');
    setDetailLoad(null);
    setDetailLoadSummary(createRequestLoadSummaryState());
    setDetailLoadState({ type: 'idle', message: '' });
  }, []);

  const updateDetailLoadStatus = useCallback(async (action) => {
    if (!detailLoad?.loadId) {
      return;
    }

    setDetailLoadState({ type: 'pending', message: `${action === 'complete' ? 'Completing' : 'Cancelling'} load...` });
    try {
      const updated = await updateRequestLoadStatus(
        detailLoad.loadId,
        action,
        operatorActorName,
      );
      setRequestLoads((currentItems) => mergeRequestLoad(currentItems, updated));
      setDetailLoad(updated);
      setDetailLoadState({ type: 'success', message: `Load '${updated.loadId}' updated.` });
    } catch (error) {
      setDetailLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : `Unable to ${action} the load.`,
      });
    }
  }, [detailLoad?.loadId, operatorActorName, setRequestLoads]);

  const voidDetailRequest = useCallback(async (requestId) => {
    setDetailLoadState({ type: 'pending', message: 'Voiding request...' });
    try {
      const { voided, replacement } = await voidWeighingRequest(requestId, {
        voidedBy: operatorActorName,
        source: operatorSource,
      });

      setRequests((currentItems) => {
        let nextItems = currentItems;
        if (voided) {
          nextItems = mergeRequests(nextItems, voided);
        }
        if (replacement) {
          nextItems = mergeRequests(nextItems, replacement);
        }
        return nextItems;
      });

      const restoredDirection = replacement?.requestDirection ?? voided?.requestDirection;
      if (restoredDirection) {
        const nextDirection = restoredDirection === 'Inbound' ? 'Inbound' : 'Outbound';
        createRequestDirectionRef.current = nextDirection;
        setCreateRequestDirection(nextDirection);
      }

      if (replacement) {
        setCurrentRequest((current) => {
          if (
            current?.requestId !== replacement.requestId
            || current?.replacesRequestId !== replacement.replacesRequestId
          ) {
            setReadings([]);
          }
          return replacement;
        });
        setRequestState({ type: 'success', message: '' });
      }

      await openLoadDetailModal(detailLoadId);
    } catch (error) {
      setDetailLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to void the request.',
      });
    }
  }, [
    createRequestDirectionRef,
    detailLoadId,
    openLoadDetailModal,
    operatorActorName,
    operatorSource,
    setCreateRequestDirection,
    setCurrentRequest,
    setReadings,
    setRequestState,
    setRequests,
  ]);

  return {
    closeLoadDetailModal,
    detailLoad,
    detailLoadId,
    detailLoadState,
    detailLoadSummary,
    openLoadDetailModal,
    updateDetailLoadStatus,
    voidDetailRequest,
  };
}
